import React, { useEffect, useRef, useState } from "react";
import { Badge, Spinner } from "flowbite-react";
import { HiOutlineExclamation } from "react-icons/hi";

import ToastNotification from "../../components/ToastNotification";
import {
  getPasswordRevealRefusal,
  revealMemberPassword,
} from "../../api/memberAccount/actions";
import {
  PASSWORD_REVEAL_OPERATOR_UNVERIFIED,
  PASSWORD_REVEAL_RETIRED,
  PasswordRevealRefusal,
  RevealMemberPasswordResponse,
} from "../../api/memberAccount/types";
import { useActorEmail } from "../../hooks/useActorEmail";
import { useOperatorToken } from "../../hooks/useOperatorToken";
import {
  isClipboardAvailable,
  writeSecretToClipboard,
} from "../../utils/clipboard";
import { customerDetailStrings } from "./CustomerDetail.strings";

const S = customerDetailStrings.header.password;

/** How long a password revealed in the header stays on screen (ADR-001). */
export const HEADER_REVEAL_AUTO_HIDE_MS = 30_000;

interface MemberPasswordHeaderFieldProps {
  email: string;
  status?: string;
}

type PendingAction = "reveal" | "copy";

/** Rejects a reveal whose request was superseded before it was sent. */
const SUPERSEDED = new Error("superseded");

/** The header copy for a typed refusal (the generic error is handled apart). */
const refusalMessage = (refusal: PasswordRevealRefusal): string => {
  switch (refusal.code) {
    case "PASSWORD_REVEAL_RATE_LIMITED":
      return refusal.scope === "target"
        ? S.refusals.rateLimitedTarget
        : S.refusals.rateLimitedOperator;
    case PASSWORD_REVEAL_RETIRED:
      return S.error;
    default:
      return S.refusals[refusal.code];
  }
};

const buttonClass =
  "inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-800 hover:border-neutral-800 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-400";

/**
 * The one-click `Password: •••••••• [Reveal] [Copy]` row of the member header
 * (ADR-001). It calls the same audited DS reveal as the Security tab, with
 * `surface: 'header'` and `intent: 'reveal' | 'copy'` (ADR-002).
 *
 * The revealed password lives ONLY in this component's state while visible:
 * never in the React Query cache, storage, the URL or a log. It is dropped
 * after HEADER_REVEAL_AUTO_HIDE_MS, on Hide, when the member changes and on
 * unmount. Copy-while-hidden keeps the answer in the click handler's closure
 * and hands it straight to the clipboard.
 */
const MemberPasswordHeaderField: React.FC<MemberPasswordHeaderFieldProps> = ({
  email,
  status,
}) => {
  const isActive = (status ?? "").toLowerCase() === "active";
  const actorEmail = useActorEmail();
  // The token is read at click time and handed to the reveal request only.
  const { hasToken, getOperatorToken, signInAgain } = useOperatorToken();
  const clipboardAvailable = isClipboardAvailable();

  const [revealed, setRevealed] = useState<RevealMemberPasswordResponse | null>(
    null
  );
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [refusal, setRefusal] = useState<PasswordRevealRefusal | null>(null);
  const [failed, setFailed] = useState(false);
  const [notStored, setNotStored] = useState(false);
  // Bumped on every request, member change and unmount so a late answer for a
  // previous action, member or an unmounted page is discarded.
  const requestRef = useRef(0);
  // Blocks a second request before React re-renders the disabled buttons.
  const pendingRef = useRef(false);

  useEffect(() => {
    setRevealed(null);
    setPending(null);
    setRefusal(null);
    setFailed(false);
    setNotStored(false);
    pendingRef.current = false;
    return () => {
      requestRef.current += 1;
      pendingRef.current = false;
    };
  }, [email]);

  useEffect(() => {
    if (!revealed) {
      return;
    }
    const timer = setTimeout(() => setRevealed(null), HEADER_REVEAL_AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [revealed]);

  const canAct =
    isActive &&
    Boolean(actorEmail) &&
    hasToken &&
    pending === null &&
    !notStored;

  /** Starts a request; returns a check that tells whether it is still the latest. */
  const beginRequest = (action: PendingAction): (() => boolean) => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    pendingRef.current = true;
    setPending(action);
    setRefusal(null);
    setFailed(false);
    return () => requestId === requestRef.current;
  };

  const endRequest = (isLatest: () => boolean) => {
    if (isLatest()) {
      pendingRef.current = false;
      setPending(null);
    }
  };

  const handleFailure = (error: unknown) => {
    const typed = getPasswordRevealRefusal(error);
    if (!typed || typed.code === PASSWORD_REVEAL_RETIRED) {
      setFailed(true);
      return;
    }
    if (typed.code === "PASSWORD_NOT_STORED") {
      setNotStored(true);
    }
    setRefusal(typed);
  };

  const requestPassword = (
    intent: PendingAction,
    isLatest: () => boolean
  ): Promise<RevealMemberPasswordResponse> =>
    getOperatorToken().then((operatorToken) => {
      if (!isLatest()) {
        throw SUPERSEDED;
      }
      return revealMemberPassword(email, {
        operatorEmail: actorEmail,
        operatorToken,
        surface: "header",
        intent,
      });
    });

  const handleReveal = async () => {
    if (!canAct || pendingRef.current) {
      return;
    }
    const isLatest = beginRequest("reveal");
    try {
      const data = await requestPassword("reveal", isLatest);
      if (!isLatest()) return;
      setRevealed(data);
    } catch (error: unknown) {
      if (!isLatest()) return;
      handleFailure(error);
    } finally {
      endRequest(isLatest);
    }
  };

  const toastOutcome = (
    outcome: "copied" | "failed",
    current: boolean | null | undefined
  ) => {
    if (outcome === "failed") {
      ToastNotification({ type: "failure", message: S.copyFailed });
      return;
    }
    ToastNotification({
      type: "success",
      message: current === false ? S.copiedStale : S.copied,
    });
  };

  const handleCopyVisible = () => {
    if (!revealed || !clipboardAvailable) {
      return;
    }
    const { password, current } = revealed;
    // No request and no timer restart: the value on screen is copied.
    void writeSecretToClipboard(Promise.resolve(password)).then((outcome) =>
      toastOutcome(outcome, current)
    );
  };

  const handleCopyHidden = () => {
    if (!canAct || !clipboardAvailable || pendingRef.current) {
      return;
    }
    const isLatest = beginRequest("copy");
    // The answer stays in this closure: it is never rendered or put in state.
    let current: boolean | null | undefined;
    const passwordPromise = requestPassword("copy", isLatest).then((data) => {
      current = data.current;
      return data.password;
    });
    // Called synchronously, before any await, so the browser still sees the
    // click's user activation.
    void writeSecretToClipboard(passwordPromise)
      .then(
        (outcome) => {
          if (!isLatest()) return;
          toastOutcome(outcome, current);
        },
        (error: unknown) => {
          if (!isLatest()) return;
          handleFailure(error);
        }
      )
      .finally(() => endRequest(isLatest));
  };

  const isVisible = revealed !== null;

  let hint: string | null = null;
  if (!isActive) {
    hint = S.inactive;
  } else if (!actorEmail) {
    hint = S.noOperator;
  } else if (!hasToken) {
    hint = S.noOperatorToken;
  }
  const showSignInAgain =
    (isActive && Boolean(actorEmail) && !hasToken) ||
    refusal?.code === PASSWORD_REVEAL_OPERATOR_UNVERIFIED;

  const message = refusal ? refusalMessage(refusal) : failed ? S.error : null;

  const revealDisabled = isVisible ? false : !canAct;
  const copyDisabled = isVisible
    ? !clipboardAvailable
    : !canAct || !clipboardAvailable;

  return (
    // Masked in Datadog Session Replay; the buttons carry fixed RUM action
    // names so no action is ever named after the password text.
    <div
      className="mt-4 flex flex-col gap-2"
      data-testid="customer-password"
      data-dd-privacy="mask"
    >
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-gray-500">{S.label}</p>
        <span
          className="font-mono text-sm font-semibold text-neutral-800"
          data-testid="customer-password-value"
        >
          {isVisible ? revealed.password : S.masked}
        </span>
        <button
          type="button"
          className={buttonClass}
          data-testid="customer-password-reveal"
          data-dd-action-name="Header password reveal"
          disabled={revealDisabled}
          aria-busy={pending === "reveal"}
          onClick={isVisible ? () => setRevealed(null) : handleReveal}
        >
          {pending === "reveal" && (
            <span aria-hidden="true">
              <Spinner size="xs" />
            </span>
          )}
          {isVisible ? S.hide : S.reveal}
        </button>
        <button
          type="button"
          className={buttonClass}
          data-testid="customer-password-copy"
          data-dd-action-name="Header password copy"
          disabled={copyDisabled}
          aria-busy={pending === "copy"}
          title={clipboardAvailable ? undefined : S.copyUnavailable}
          onClick={isVisible ? handleCopyVisible : handleCopyHidden}
        >
          {pending === "copy" && (
            <span aria-hidden="true">
              <Spinner size="xs" />
            </span>
          )}
          {S.copy}
        </button>
      </div>

      {isVisible && (
        <div className="flex flex-wrap items-center gap-2">
          {revealed.current === false && (
            <Badge
              color="warning"
              icon={HiOutlineExclamation}
              size="sm"
              className="w-fit"
              data-testid="customer-password-stale"
            >
              {S.staleBadge}
            </Badge>
          )}
          {revealed.isLikelyAutoGenerated && (
            <Badge
              color="warning"
              icon={HiOutlineExclamation}
              size="sm"
              className="w-fit"
              data-testid="customer-password-autogenerated"
            >
              {S.autoGeneratedBadge}
            </Badge>
          )}
          <p
            className="text-xs text-gray-400"
            data-testid="customer-password-auto-hide"
          >
            {S.autoHideNote}
          </p>
        </div>
      )}

      {hint && (
        <p className="text-xs text-gray-500" data-testid="customer-password-hint">
          {hint}
        </p>
      )}

      {message && (
        <p
          className="text-sm text-red-700"
          role="alert"
          data-testid="customer-password-message"
          {...(refusal ? { "data-code": refusal.code } : {})}
        >
          {message}
        </p>
      )}

      {showSignInAgain && (
        <div>
          <button
            type="button"
            className={buttonClass}
            data-testid="customer-password-sign-in"
            data-dd-action-name="Header password sign in again"
            onClick={signInAgain}
          >
            {S.signInAgain}
          </button>
        </div>
      )}
    </div>
  );
};

export default MemberPasswordHeaderField;
